/**
 * ConfidenceBreakdownCard Component
 *
 * Displays detailed confidence score breakdown for a mention
 * Shared component used by both MentionReviewDetailPage and BusinessMentionCard
 */
import React from 'react';
import { Card, Stack, Group, Text, Badge, Box, Divider, Alert } from '@mantine/core';
import { IconPercentage, IconAlertCircle } from '@tabler/icons-react';
import type { ConfidenceBreakdownDto } from '@/domain/entities/ConfidenceBreakdown';
import { getMatchTypeLabel, getMatchTypeColor } from '@/domain/entities/ConfidenceBreakdown';
import { getConfidenceLevel } from '@/presentation/components/admin/MentionReview/MentionReviewSection.types';

export interface ConfidenceBreakdownCardProps {
  /**
   * Confidence breakdown data (null for old mentions without breakdown)
   */
  confidenceBreakdown: ConfidenceBreakdownDto | null;

  /**
   * Fallback confidence score if breakdown is null
   */
  matchConfidenceScore: number;

  /**
   * Whether to show the card border
   */
  withBorder?: boolean;

  /**
   * Card padding
   */
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Card shadow
   */
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * ConfidenceBreakdownCard - Reusable confidence score display
 */
export const ConfidenceBreakdownCard: React.FC<ConfidenceBreakdownCardProps> = ({
  confidenceBreakdown,
  matchConfidenceScore,
  withBorder = true,
  padding = 'md',
  shadow = 'sm'
}): React.ReactElement => {
  // Calculate display values
  const confidenceScore = confidenceBreakdown?.finalScore ?? matchConfidenceScore;
  const confidenceLevel = getConfidenceLevel(confidenceScore);

  return (
    <Card withBorder={withBorder} padding={padding} shadow={shadow}>
      <Stack gap="sm">
        <Group gap="xs">
          <IconPercentage size={20} />
          <Text size="md" fw={500}>
            Confidence Score Breakdown
          </Text>
        </Group>

        {confidenceBreakdown ? (
          <Box>
            {/* Match Type Badge */}
            <Group gap="xs" mb="md">
              <Badge color={getMatchTypeColor(confidenceBreakdown.matchType)} size="lg">
                {getMatchTypeLabel(confidenceBreakdown.matchType)}
              </Badge>
              <Badge color={confidenceLevel.color} size="lg">
                {confidenceBreakdown.confidenceLevel}
              </Badge>
            </Group>

            {/* Score Components */}
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm">Base Score ({confidenceBreakdown.matchType}):</Text>
                <Text size="sm" fw={500} c="green">
                  +{confidenceBreakdown.baseScore}%
                </Text>
              </Group>

              {confidenceBreakdown.nerBoost > 0 && (
                <Group justify="space-between">
                  <Text size="sm">NER Validation Boost:</Text>
                  <Text size="sm" fw={500} c="green">
                    +{confidenceBreakdown.nerBoost}%
                  </Text>
                </Group>
              )}

              {confidenceBreakdown.contextBoost > 0 && (
                <Group justify="space-between">
                  <Text size="sm">Context Boost:</Text>
                  <Text size="sm" fw={500} c="green">
                    +{confidenceBreakdown.contextBoost}%
                  </Text>
                </Group>
              )}

              {confidenceBreakdown.penalties.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="sm" fw={500} c="red">
                    Penalties:
                  </Text>
                  {confidenceBreakdown.penalties.map((penalty, index) => (
                    <Box key={index} pl="md">
                      <Group justify="space-between">
                        <Text size="sm">{penalty.type}:</Text>
                        <Text size="sm" fw={500} c="red">
                          -{penalty.amount}%
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" fs="italic">
                        {penalty.reason}
                      </Text>
                    </Box>
                  ))}
                </>
              )}

              <Divider my="xs" />

              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Final Score:
                </Text>
                <Text size="lg" fw={700} c={confidenceLevel.color}>
                  {confidenceBreakdown.finalScore}%
                </Text>
              </Group>

              <Text size="xs" c="dimmed" mt="xs">
                {confidenceBreakdown.explanation}
              </Text>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Text size="sm" mb="xs">
              <strong>Final Score:</strong> {confidenceScore}%
            </Text>
            <Text size="sm" mb="xs">
              <strong>Confidence Level:</strong> {confidenceLevel.label}
            </Text>
            <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light" radius="sm">
              <Text size="xs">
                <strong>Note:</strong> This mention was processed before confidence breakdown
                tracking was added. New mentions will show detailed breakdowns with match type,
                boosts, and penalties.
              </Text>
            </Alert>
          </Box>
        )}
      </Stack>
    </Card>
  );
};
